import React, { useState, useRef } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Progress, Badge, Table, Spinner } from "reactstrap"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import api from "API/Api"

const BulkImportExport = ({ data, columns, sampleData, apiEndpoint, entityName, onImportComplete, showExport = true, showImport = true }) => {
  const [importModal, setImportModal] = useState(false)
  const [parsedRows, setParsedRows] = useState([])
  const [errors, setErrors] = useState([])
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [importResult, setImportResult] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const fileRef = useRef(null)

  // ──── EXPORT ────
  const handleExport = () => {
    if (!data || data.length === 0) return alert("No data to export")
    const headers = columns.map(c => c.label)
    const rows = data.map(row => columns.map(c => row[c.key] ?? ""))
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])

    // Auto-fit column widths
    ws["!cols"] = columns.map((c, i) => ({
      wch: Math.max(c.label.length, ...rows.map(r => String(r[i] || "").length), 10) + 2
    }))

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, entityName)
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
    saveAs(new Blob([buf], { type: "application/octet-stream" }), `${entityName}_Export.xlsx`)
  }

  // ──── SAMPLE DOWNLOAD ────
  const handleSampleDownload = () => {
    const headers = columns.map(c => c.label)
    const examples = (sampleData || [{}]).map(row => columns.map(c => row[c.key] ?? ""))
    const ws = XLSX.utils.aoa_to_sheet([headers, ...examples])

    ws["!cols"] = columns.map(c => ({ wch: Math.max(c.label.length, 15) + 2 }))

    // Add comments for required fields
    columns.forEach((c, i) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: i })
      if (!ws[cellRef]) ws[cellRef] = { t: "s", v: c.label }
      if (c.required) ws[cellRef].v = c.label + " *"
    })

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Template")
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
    saveAs(new Blob([buf], { type: "application/octet-stream" }), `${entityName}_Template.xlsx`)
  }

  // ──── FILE PARSE ────
  const parseFile = (file) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const raw = XLSX.utils.sheet_to_json(ws, { defval: "" })

        // Map Excel headers to DB keys
        const headerMap = {}
        columns.forEach(c => {
          headerMap[c.label.toLowerCase().replace(/ \*/g, "").trim()] = c.key
          headerMap[c.key.toLowerCase()] = c.key
        })

        const rows = []
        const errs = []

        raw.forEach((row, idx) => {
          const mapped = {}
          Object.keys(row).forEach(h => {
            const key = headerMap[h.toLowerCase().trim()]
            if (key) mapped[key] = String(row[h]).trim()
          })

          // Validate required fields
          const missing = columns.filter(c => c.required && !mapped[c.key])
          if (missing.length > 0) {
            errs.push({ row: idx + 2, message: `Missing: ${missing.map(m => m.label).join(", ")}` })
            mapped._hasError = true
            mapped._errorMsg = `Missing: ${missing.map(m => m.label).join(", ")}`
          }
          mapped._rowNum = idx + 2
          rows.push(mapped)
        })

        setParsedRows(rows)
        setErrors(errs)
        setImportResult(null)
      } catch (err) {
        alert("Failed to parse file: " + err.message)
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) parseFile(file)
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) parseFile(file)
  }

  // ──── IMPORT ────
  const handleImport = async () => {
    const validRows = parsedRows.filter(r => !r._hasError)
    if (validRows.length === 0) return alert("No valid rows to import")

    setImporting(true)
    setProgress(0)
    let success = 0, failed = 0
    const failedRows = []

    for (let i = 0; i < validRows.length; i++) {
      const row = { ...validRows[i] }
      const rowNum = row._rowNum
      delete row._rowNum
      delete row._hasError
      delete row._errorMsg
      try {
        await api.post(apiEndpoint, row)
        success++
      } catch (e) {
        failed++
        failedRows.push({ row: rowNum, message: e.response?.data?.message || e.message || "Unknown error" })
      }
      setProgress(Math.round(((i + 1) / validRows.length) * 100))
    }

    setImportResult({ success, failed, failedRows })
    setImporting(false)
    if (success > 0 && onImportComplete) onImportComplete()
  }

  const closeModal = () => {
    setImportModal(false)
    setParsedRows([])
    setErrors([])
    setImportResult(null)
    setProgress(0)
    if (fileRef.current) fileRef.current.value = ""
  }

  const validCount = parsedRows.filter(r => !r._hasError).length
  const errorCount = parsedRows.filter(r => r._hasError).length

  // ──── EXPORT ERRORS ────
  const downloadErrors = () => {
    const allErrors = [...errors, ...(importResult?.failedRows || [])]
    const ws = XLSX.utils.json_to_sheet(allErrors.map(e => ({ "Row #": e.row, "Error": e.message })))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Errors")
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
    saveAs(new Blob([buf], { type: "application/octet-stream" }), `${entityName}_Import_Errors.xlsx`)
  }

  return (
    <>
      {/* Buttons */}
      <div style={{ display: "inline-flex", gap: "6px" }}>
        {showExport && (
          <button title="Export to Excel" onClick={handleExport}
            style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#17a2b8", color: "#fff",
              border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer" }}>
            <i className="fas fa-download"></i>
          </button>
        )}
        {showImport && (
          <button title="Import from Excel" onClick={() => setImportModal(true)}
            style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#6f42c1", color: "#fff",
              border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer" }}>
            <i className="fas fa-file-upload"></i>
          </button>
        )}
      </div>

      {/* Import Modal */}
      <Modal isOpen={importModal} toggle={closeModal} size="lg" style={{ maxWidth: "900px" }}>
        <ModalHeader toggle={closeModal} style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff", border: "none" }}>
          <i className="fas fa-file-import me-2"></i>Import {entityName}
        </ModalHeader>
        <ModalBody style={{ padding: "24px" }}>

          {/* Drag & Drop Zone */}
          {parsedRows.length === 0 && (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? "#6366f1" : "#cbd5e1"}`,
                borderRadius: "12px", padding: "48px 24px", textAlign: "center",
                cursor: "pointer", transition: "all 0.2s ease",
                background: dragOver ? "#6366f115" : "#fafafa"
              }}
            >
              <i className="fas fa-cloud-upload-alt" style={{ fontSize: "48px", color: dragOver ? "#6366f1" : "#94a3b8", marginBottom: "12px", display: "block" }}></i>
              <h5 style={{ color: "#475569", marginBottom: "6px" }}>Drag & Drop Excel File Here</h5>
              <p style={{ color: "#94a3b8", marginBottom: "12px" }}>or click to browse (.xlsx, .xls)</p>
              <button className="btn btn-outline-primary btn-sm" onClick={e => { e.stopPropagation(); handleSampleDownload() }}>
                <i className="fas fa-download me-1"></i>Download Sample Template First
              </button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={handleFileSelect} />
            </div>
          )}

          {/* Preview Table */}
          {parsedRows.length > 0 && !importResult && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                <div>
                  <Badge color="primary" className="me-2" style={{ fontSize: "12px" }}>{parsedRows.length} Total Rows</Badge>
                  <Badge color="success" className="me-2" style={{ fontSize: "12px" }}>{validCount} Valid</Badge>
                  {errorCount > 0 && <Badge color="danger" style={{ fontSize: "12px" }}>{errorCount} Errors</Badge>}
                </div>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => { setParsedRows([]); setErrors([]); if (fileRef.current) fileRef.current.value = "" }}>
                  <i className="fas fa-times me-1"></i>Clear
                </button>
              </div>
              <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <Table size="sm" className="table-hover mb-0" style={{ fontSize: "12px" }}>
                  <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1 }}>
                    <tr>
                      <th style={{ fontSize: "10px", color: "#64748b" }}>Row</th>
                      <th style={{ fontSize: "10px", color: "#64748b" }}>Status</th>
                      {columns.slice(0, 6).map(c => <th key={c.key} style={{ fontSize: "10px", color: "#64748b" }}>{c.label}</th>)}
                      {columns.length > 6 && <th style={{ fontSize: "10px", color: "#64748b" }}>...</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.slice(0, 50).map((row, i) => (
                      <tr key={i} style={{ background: row._hasError ? "#fef2f2" : "" }}>
                        <td style={{ color: "#94a3b8" }}>{row._rowNum}</td>
                        <td>
                          {row._hasError
                            ? <span style={{ color: "#ef4444", fontSize: "11px" }} title={row._errorMsg}><i className="fas fa-times-circle"></i></span>
                            : <span style={{ color: "#22c55e", fontSize: "11px" }}><i className="fas fa-check-circle"></i></span>
                          }
                        </td>
                        {columns.slice(0, 6).map(c => <td key={c.key}>{row[c.key] || <span style={{ color: "#cbd5e1" }}>—</span>}</td>)}
                        {columns.length > 6 && <td style={{ color: "#94a3b8" }}>+{columns.length - 6}</td>}
                      </tr>
                    ))}
                    {parsedRows.length > 50 && (
                      <tr><td colSpan={8 + 1} className="text-center text-muted" style={{ fontSize: "11px" }}>+ {parsedRows.length - 50} more rows...</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
              {importing && (
                <div style={{ marginTop: "16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                    <Spinner size="sm" color="primary" />
                    <span style={{ fontSize: "13px", color: "#475569" }}>Importing... {progress}%</span>
                  </div>
                  <Progress value={progress} color="primary" style={{ height: "8px", borderRadius: "4px" }} />
                </div>
              )}
            </>
          )}

          {/* Import Results */}
          {importResult && (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <i className={`fas ${importResult.failed === 0 ? "fa-check-circle" : "fa-exclamation-circle"}`}
                style={{ fontSize: "48px", color: importResult.failed === 0 ? "#22c55e" : "#f59e0b", marginBottom: "12px", display: "block" }}></i>
              <h5 style={{ color: "#334155" }}>Import Complete</h5>
              <div style={{ display: "flex", justifyContent: "center", gap: "24px", marginTop: "16px" }}>
                <div style={{ background: "#f0fdf4", borderRadius: "8px", padding: "12px 24px", textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#22c55e" }}>{importResult.success}</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>Imported</div>
                </div>
                <div style={{ background: importResult.failed > 0 ? "#fef2f2" : "#f0fdf4", borderRadius: "8px", padding: "12px 24px", textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: importResult.failed > 0 ? "#ef4444" : "#22c55e" }}>{importResult.failed}</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>Failed</div>
                </div>
              </div>
              {importResult.failed > 0 && (
                <button className="btn btn-outline-danger btn-sm mt-3" onClick={downloadErrors}>
                  <i className="fas fa-download me-1"></i>Download Error Report
                </button>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ borderTop: "1px solid #e2e8f0" }}>
          <Button color="secondary" size="sm" onClick={closeModal}>Close</Button>
          {parsedRows.length > 0 && !importResult && (
            <Button color="primary" size="sm" onClick={handleImport} disabled={importing || validCount === 0}>
              {importing ? <><Spinner size="sm" className="me-1" />Importing...</> : <><i className="fas fa-upload me-1"></i>Import {validCount} Records</>}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  )
}

export default BulkImportExport
